import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const NavigationGuardContext = createContext(null);

export function NavigationGuardProvider({ children }) {
  const navigate = useNavigate();
  const [isBlocked, setIsBlocked] = useState(false);
  const pendingNavRef = useRef(null);
  const isGuardedRef = useRef(false);

  const guardedNavigate = useCallback((to, options) => {
    if (isGuardedRef.current) {
      pendingNavRef.current = { to, options };
      setIsBlocked(true);
    } else {
      navigate(to, options);
    }
  }, [navigate]);

  const enableGuard = useCallback(() => { isGuardedRef.current = true; }, []);
  const disableGuard = useCallback(() => { isGuardedRef.current = false; }, []);

  const proceed = useCallback(() => {
    setIsBlocked(false);
    isGuardedRef.current = false;
    if (pendingNavRef.current) {
      navigate(pendingNavRef.current.to, pendingNavRef.current.options);
      pendingNavRef.current = null;
    }
  }, [navigate]);

  const reset = useCallback(() => {
    setIsBlocked(false);
    pendingNavRef.current = null;
  }, []);

  return (
    <NavigationGuardContext.Provider
      value={{ guardedNavigate, enableGuard, disableGuard, isBlocked, proceed, reset }}
    >
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  return useContext(NavigationGuardContext);
}